/**
 * A collection of date formatting utilities for React applications
 * that format dates in French style with different levels of detail.
 * 
 * UPDATED: Now works with string datetime format "YYYY-MM-DD HH:MM"
 */

/**
 * Checks if the time is midnight (00:00)
 * @param {string} dateTimeString - DateTime string in format "YYYY-MM-DD HH:MM"
 * @returns {boolean} - True if the time is midnight
 */
export const isMidnight = (dateTimeString) => {
  if (!dateTimeString || typeof dateTimeString !== 'string') return false;
  
  // Extract time portion
  const timePart = dateTimeString.includes(' ') 
    ? dateTimeString.split(' ')[1] 
    : null;
  
  return timePart === '00:00' || timePart === null;
};

/**
 * Converts string datetime to Date object for formatting
 * @param {string} dateTimeString - DateTime string in format "YYYY-MM-DD HH:MM" or "YYYY-MM-DD"
 * @returns {Date} - Date object
 */
const parseDateTime = (dateTimeString) => {
  if (!dateTimeString || typeof dateTimeString !== 'string') {
    console.warn('Invalid dateTimeString:', dateTimeString);
    return new Date();
  }
  
  // If it already has a T (ISO format), use as-is
  if (dateTimeString.includes('T')) {
    return new Date(dateTimeString);
  }
  
  // If it has time part, add T for ISO format
  if (dateTimeString.includes(' ')) {
    return new Date(dateTimeString.replace(' ', 'T'));
  }
  
  // Date only, add midnight time
  return new Date(`${dateTimeString}T00:00:00`);
};

/**
 * Formats date in short French style (01/01/2025)
 * @param {string} dateTimeString - DateTime string in format "YYYY-MM-DD HH:MM" or "YYYY-MM-DD"
 * @returns {string} - Formatted date string
 */
export const formatShortDate = (dateTimeString) => {
  const date = parseDateTime(dateTimeString);
  
  // Format: DD/MM/YYYY
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formats date in medium French style (1 janvier 2025)
 * @param {string} dateTimeString - DateTime string in format "YYYY-MM-DD HH:MM" or "YYYY-MM-DD"
 * @returns {string} - Formatted date string
 */
export const formatMediumDate = (dateTimeString) => {
  const date = parseDateTime(dateTimeString);
  
  // Format: D month YYYY
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Formats date in long French style with day of week (lundi 1 janvier 2025)
 * @param {string} dateTimeString - DateTime string in format "YYYY-MM-DD HH:MM" or "YYYY-MM-DD"
 * @returns {string} - Formatted date string
 */
export const formatLongDate = (dateTimeString) => {
  const date = parseDateTime(dateTimeString);
  
  // Format: weekday D month YYYY
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Formats only the time in French style (14:30)
 * Returns null if time is midnight
 * @param {string} dateTimeString - DateTime string in format "YYYY-MM-DD HH:MM"
 * @returns {string|null} - Formatted time string or null if midnight
 */
export const formatTimeOnly = (dateTimeString) => {
  if (!dateTimeString || typeof dateTimeString !== 'string' || isMidnight(dateTimeString)) {
    return null;
  }
  
  // Extract time portion from string
  const timePart = dateTimeString.includes(' ') 
    ? dateTimeString.split(' ')[1] 
    : null;
  
  return timePart; // Already in HH:MM format
};

/**
 * Formats date and time in French style
 * If time is midnight, returns only the date
 * @param {string} dateTimeString - DateTime string in format "YYYY-MM-DD HH:MM" or "YYYY-MM-DD"
 * @param {string} dateFormat - 'short', 'medium', or 'long'
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (dateTimeString, dateFormat = 'medium') => {
  // If midnight, return only the date
  if (isMidnight(dateTimeString)) {
    switch (dateFormat) {
      case 'short':
        return formatShortDate(dateTimeString);
      case 'long':
        return formatLongDate(dateTimeString);
      case 'medium':
      default:
        return formatMediumDate(dateTimeString);
    }
  }
  
  // Otherwise, return date and time
  const date = parseDateTime(dateTimeString);
  
  let dateOptions;
  switch (dateFormat) {
    case 'short':
      dateOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      break;
    case 'long':
      dateOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      break;
    case 'medium':
    default:
      dateOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
  }
  
  return date.toLocaleString('fr-FR', dateOptions);
};

/**
 * A React component for displaying a formatted date
 * @param {Object} props - Component props
 * @param {string} props.dateString - DateTime string in format "YYYY-MM-DD HH:MM" or "YYYY-MM-DD"
 * @param {string} props.format - 'short', 'medium', 'long', or 'datetime'
 * @returns {JSX.Element} - Rendered component
 */
const FormatDate = ({ dateString, format = 'medium' }) => {
  if (!dateString) return null;
  
  let formattedDate;
  
  switch (format) {
    case 'short':
      formattedDate = formatShortDate(dateString);
      break;
    case 'long':
      formattedDate = formatLongDate(dateString);
      break;
    case 'datetime':
      formattedDate = formatDateTime(dateString, 'medium');
      break;
    case 'time':
      formattedDate = formatTimeOnly(dateString);
      if (formattedDate === null) return null; // Don't render if midnight
      break;
    case 'medium':
    default:
      formattedDate = formatMediumDate(dateString);
  }
  
  // Convert to ISO format for datetime attribute
  const isoDateTime = dateString.includes('T') 
    ? dateString 
    : dateString.replace(' ', 'T');
  
  return <time dateTime={isoDateTime}>{formattedDate}</time>;
};

export default FormatDate;

// Example usage:
/*
import FormatDate, { 
  formatShortDate, 
  formatMediumDate, 
  formatLongDate, 
  formatDateTime,
  formatTimeOnly
} from './path/to/FormatDate';

// As component with new string format
<FormatDate dateString="2025-03-13 14:30" format="medium" />
<FormatDate dateString="2025-03-13 00:00" format="medium" /> // Won't show time
<FormatDate dateString="2025-03-13" format="medium" /> // Date only

// As function
const shortDate = formatShortDate("2025-03-13 14:30"); // "13/03/2025"
const dateWithTime = formatDateTime("2025-03-13 14:30", "long"); // "mercredi 13 mars 2025 à 14:30"
const timeOnly = formatTimeOnly("2025-03-13 14:30"); // "14:30"
const timeOnlyMidnight = formatTimeOnly("2025-03-13 00:00"); // null
*/